import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowLeft, Award, BookOpen, Globe, Users, Heart } from 'lucide-react';
import Footer from '../components/shared/Footer';

export default function MeetFounder() {
  const { t, i18n } = useTranslation('meetFounder');
  const homeT = useTranslation('home').t;
  const isRTL = i18n.language === 'ar';

  return (
    <div className={`min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* Custom Hero Section with Founder Picture */}
      <section className="relative min-h-screen overflow-hidden bg-slate-900">
        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-50 bg-transparent">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Logo */}
              <Link to="/" className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT" 
                  className="w-10 h-10 rounded-xl"
                />
                <span className="text-white text-xl font-bold tracking-tight">FaheemGPT</span>
              </Link>
              
              {/* Navigation */}
              <nav className={`hidden md:flex items-center gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link to="/about" className="text-white/80 hover:text-white transition-colors font-medium">{homeT('nav.about')}</Link>
                <Link to="/meet-founder" className="text-white hover:text-white transition-colors font-medium">{homeT('nav.meetFounder', 'Meet Founder')}</Link>
                <Link to="/partners" className="text-white/80 hover:text-white transition-colors font-medium">{homeT('nav.partners')}</Link>
                <Link to="/how-it-works" className="text-white/80 hover:text-white transition-colors font-medium">{homeT('nav.howItWorks')}</Link>
                <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">{homeT('nav.login', 'Login')}</Link>
              </nav>

              {/* Language Switcher */}
              <button 
                onClick={() => i18n.changeLanguage(isRTL ? 'en' : 'ar')}
                className="text-white/80 hover:text-white transition-colors text-sm font-medium"
              >
                {isRTL ? 'EN' : 'العربية'}
              </button>
            </div>
          </div>
        </header>

        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)' }}
        >
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>
        
        {/* Content with Founder Picture */}
        <div className="relative h-screen flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <div className={`flex flex-col md:flex-row items-center justify-center gap-12 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
              {/* Founder Picture - Small beside text */}
              <div className="relative flex-shrink-0">
                {/* Decorative ring */}
                <div className="absolute -inset-3 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full blur-lg"></div>
                {/* Picture container */}
                <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/qtoz63pa_yazid.jpg" 
                    alt={t('hero.name')}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Text Content - Centered */}
              <div className={`text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                  {t('hero.name')}
                </h1>
                <div className="space-y-1 mb-4">
                  <p className="text-xl md:text-2xl text-blue-400 font-semibold">{t('hero.degree')}</p>
                  <p className="text-lg text-white/70">{t('hero.university')}</p>
                </div>
                <p className="text-lg text-white/80 leading-relaxed max-w-xl">
                  {t('hero.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-8 z-20">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto space-y-20">

            {/* Research & Education */}
            <div className="space-y-8">
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    <BookOpen className="w-8 h-8 text-blue-600" />
                    <h2 className="text-3xl font-bold text-slate-900">{t('research.title')}</h2>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm space-y-6" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <div className="space-y-2">
                        <h3 className="text-xl font-bold text-slate-800">{t('research.gpt.title')}</h3>
                        <p className="text-slate-600">
                            {t('research.gpt.description')}
                        </p>
                    </div>
                    <div className="h-px bg-slate-100" />
                    <div className="space-y-2">
                        <h3 className="text-xl font-bold text-slate-800">{t('research.fateen.title')}</h3>
                        <p className="text-slate-600">
                            {t('research.fateen.description')}
                        </p>
                    </div>
                    <div className="h-px bg-slate-100" />
                    <div className="space-y-2">
                        <h3 className="text-xl font-bold text-slate-800">{t('research.conference.title')}</h3>
                        <p className="text-slate-600">
                            {t('research.conference.description')} <span className="italic text-slate-800">&ldquo;{t('research.conference.paperTitle')}&rdquo;</span> {t('research.conference.descriptionEnd')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Mentorship & Voluntary Work */}
            <div className="space-y-8">
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    <Heart className="w-8 h-8 text-red-500" />
                    <h2 className="text-3xl font-bold text-slate-900">{t('mentorship.title')}</h2>
                </div>
                
                <p className="text-lg text-slate-600 leading-relaxed" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    &ldquo;{t('mentorship.quote')}&rdquo;
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="bg-white border-slate-200" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <CardContent className="pt-6">
                            <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                                <Award className="w-10 h-10 text-yellow-500 mb-4" />
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">{t('mentorship.isef.title')}</h3>
                            <p className="text-sm text-slate-600 mb-2">
                                {t('mentorship.isef.year2017')}
                            </p>
                            <p className="text-sm text-slate-600">
                                {t('mentorship.isef.year2018')}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border-slate-200" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <CardContent className="pt-6">
                            <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                                <Award className="w-10 h-10 text-slate-400 mb-4" />
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">{t('mentorship.hackathons.title')}</h3>
                            <p className="text-sm text-slate-600">
                                {t('mentorship.hackathons.description')}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border-slate-200" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <CardContent className="pt-6">
                            <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                                <Globe className="w-10 h-10 text-green-500 mb-4" />
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">{t('mentorship.currentProjects.title')}</h3>
                            <p className="text-sm text-slate-600">
                                {t('mentorship.currentProjects.description')}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-6" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Users className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                        <div>
                            <h3 className="font-bold text-slate-900 text-lg">{t('mentorship.judge.title')}</h3>
                            <p className="text-slate-600 mt-2">
                                {t('mentorship.judge.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

        </div>
      </section>

      {/* Footer - Same as Homepage */}
      <Footer />
    </div>
  );
}
